import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { postData } from "../../services/request"

const AdminNewUser = () => {
  const navigate = useNavigate()
  const [name, setName] = useState("")
  const [username, setUsername] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [password, setPassword] = useState("")
  const [role, setRole] = useState("ADMIN")
  const [status, setStatus] = useState(1)
  const [btnState, setBtnState] = useState("Create Admin")
  const [error, setError] = useState("")
  const [genMsg, setGenMsg] = useState("")

  const validateForm = () => {
    setError("")
    if (!name || name.trim().length < 2) {
      setError("Name must be at least 2 characters")
      return false
    }
    if (!username || username.trim().length < 2) {
      setError("Username must be at least 2 characters")
      return false
    }
    if (!phoneNumber || phoneNumber.length !== 11 || !/^\d+$/.test(phoneNumber)) {
      setError("Phone number must be exactly 11 digits")
      return false
    }
    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters")
      return false
    }
    if (!role) {
      setError("Role is required")
      return false
    }
    return true
  }

  const submit = async () => {
    setGenMsg("")
    setError("")
    if (!validateForm()) return

    setBtnState("Creating...")
    const url = `${getAPIBaseURL()}/v1/admins`
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
    const params = new URLSearchParams()
    params.append('name', name.trim())
    params.append('username', username.trim())
    params.append('phoneNumber', phoneNumber.trim())
    params.append('password', password)
    params.append('role', role)
    params.append('status', status.toString())

    const res = await postData(url, headers, params.toString())
    setBtnState("Create Admin")

    if (res.error == "") {
      if (res.result.status === 200 || res.result.status === 201) {
        setGenMsg("Admin created successfully")
        setTimeout(() => {
          navigate("/admin/app/system-users")
        }, 1500)
      } else {
        setError(res.result.data?.message || 'Could not create admin')
      }
    } else {
      setError(res.result?.data?.message || 'Network error')
    }
  }

  return (
    <section className="mx-auto max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Create New Admin</h1>
        <button
          onClick={() => navigate("/admin/app/system-users")}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          ← Back
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-5">
          <h2 className="text-lg font-semibold text-gray-900">Admin Information</h2>
          <p className="mt-1 text-sm text-gray-600">Fill in the details to create a new admin user</p>
        </div>

        <div className="px-6 py-6 space-y-6">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">{error}</div>
          ) : null}
          {genMsg ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">{genMsg}</div>
          ) : null}

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="name" className="mb-2 block text-sm font-medium text-gray-900">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="username" className="mb-2 block text-sm font-medium text-gray-900">
                Username <span className="text-red-500">*</span>
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="johndoe"
              />
            </div>

            <div>
              <label htmlFor="phoneNumber" className="mb-2 block text-sm font-medium text-gray-900">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 11))}
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="08012345678"
                maxLength={11}
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-gray-900">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
                placeholder="Minimum 6 characters"
              />
            </div>

            <div>
              <label htmlFor="role" className="mb-2 block text-sm font-medium text-gray-900">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
              >
                <option value="ADMIN">ADMIN</option>
                <option value="SUPER_ADMIN">SUPER_ADMIN</option>
              </select>
            </div>

            <div>
              <label htmlFor="status" className="mb-2 block text-sm font-medium text-gray-900">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                id="status"
                value={status}
                onChange={(e) => setStatus(Number(e.target.value))}
                className="block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 transition-colors focus:border-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-0"
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={submit}
              disabled={btnState === "Creating..."}
              className="rounded-lg bg-black px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {btnState}
            </button>
            <button
              onClick={() => navigate("/admin/app/system-users")}
              className="rounded-lg border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AdminNewUser
